import { q12025Langs, startI18N } from "c7-i18n";
import { createElement } from "react";
import ReactDOM, { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";

import { logger } from "c7-logger";

import type { ThemePartial } from "chameleon/lib/theme/theme.css";

import { CriticalError } from "checkout-experience/layouts/critical-error";
import { usePubSub } from "checkout-query-client/hooks/use-pub-sub";
import {
  Announcement,
  PubSubMessage,
  PubSubNamespace,
} from "checkout-query-client/publish-subcriber/types";
import {
  CheckoutIntentPayload,
  getJWTPayloadJSON,
} from "checkout-query-client/utils/jwt-payload";

import {
  endDatadogSession,
  sendCustomAction,
  sendCustomTiming,
  startDatadog,
  stopDatadogUserTracking,
} from "datadog-loader/loader";

import { updateIntentCart } from "drop-in-ui/api/requests/update-intent-cart";
import { datadogConfig } from "drop-in-ui/components/datadog/config";
import { InitWrapperWithProvider } from "drop-in-ui/components/init-wrapper/init-wrapper-with-provider";
import { InitParams } from "drop-in-ui/components/init-wrapper/types";
import {
  SPLITIO_AUTHORIZATIONKEY,
  TARGET,
  UI_ROOT,
} from "drop-in-ui/utils/configs";
import { VERSION } from "drop-in-ui/utils/version/version";

import { Cart, CookiePolicy } from "frontend-client";

import {
  SplitIOSDKLoader,
  getSplitIOFlag,
  getSplitIOSDKPromise,
} from "splitio-loader";
import { getIsFeatureEnabled } from "splitio-loader/src/utils/get-split-io-flag";

const customMetrics: {
  staticLoaderStart?: number;
  staticLoaderApiFetched?: number;
  staticLoaderDIULoaded?: number;
} = {};
export default class DropInUI {
  static VERSION: string = VERSION;
  /**
   * The ID for where you want to render the form
   */
  root: ReactDOM.Root | undefined;
  /**
   * Theme values to override for style customization
   */
  themeValueOverrides?: ThemePartial | undefined;
  /**
   * The jwt for making checkout-api calls
   */
  jwt: string | null | undefined;
  /**
   * Target El
   */
  elTarget: Element | null = null;
  /**
   *
   * Cookie Policy - Merchant provided user cookie policy consent
   */
  cookiePolicy?: CookiePolicy;
  /**
   *
   * Controlled Submit - Merchant will programmatically control when form is submitted
   */
  controlledSubmit?: boolean = false;
  /**
   *
   *  When in an iframe Apple this value will be used as a paying domain
   */
  iframePayDomain?: string;

  constructor(params?: InitParams) {
    // Clear any current subscribers since this is a new instance
    this.clearAllSubscribers();
    this.runVendors(params);
    this.shouldInit(params);
    const { mid } =
      getJWTPayloadJSON<CheckoutIntentPayload>(this.jwt ?? "") ?? {};
    void SplitIOSDKLoader({
      authKey: SPLITIO_AUTHORIZATIONKEY || "",
      userKey: mid,
    }).then(() => {
      sendCustomAction({
        name: "Split.IO",
        context: {
          message: "Success DIU",
          merchantId: mid,
        },
      });
    });
    this.controlledSubmit = params?.controlledSubmit;
    this.iframePayDomain = params?.iframePayDomain;
  }

  static metrics(opts: {
    STATIC_LOADER_START: number;
    STATIC_LOADER_API_FETCHED: number;
    DIU_LOADED: number;
  }) {
    customMetrics.staticLoaderStart = opts.STATIC_LOADER_START;
    customMetrics.staticLoaderApiFetched = opts.STATIC_LOADER_API_FETCHED;
    customMetrics.staticLoaderDIULoaded = opts.DIU_LOADED;
  }

  // This is a function to check if the merchant still can get messages from pubSub.  Primarily this is for debugging
  static pubSubPing(): void {
    // Fire on next digest cycle
    setTimeout(() => {
      usePubSub().publish({
        level: "debug",
        namespace: PubSubNamespace.debug,
        message: PubSubMessage.PubSubPing,
        payload: { message: `Ping at ${Date.now()}` },
      });
    });
  }

  private runVendors(params?: InitParams): void {
    if (params?.cookiePolicy !== CookiePolicy.ONLY_NECESSARY) {
      // @ts-ignore
      startDatadog(datadogConfig);
    } else {
      // in case something has already fired it
      stopDatadogUserTracking();
    }
    if (customMetrics.staticLoaderStart) {
      sendCustomTiming({
        name: "staticLoaderStart",
        timeInMs: customMetrics.staticLoaderStart,
      });
    }
    if (customMetrics.staticLoaderApiFetched) {
      sendCustomTiming({
        name: "staticLoaderApiFetched",
        timeInMs: customMetrics.staticLoaderApiFetched,
      });
    }
    if (customMetrics.staticLoaderDIULoaded) {
      sendCustomTiming({
        name: "staticLoaderDIULoaded",
        timeInMs: customMetrics.staticLoaderDIULoaded,
      });
    }
  }

  logError(error: Error, errorInfo: React.ErrorInfo) {
    logger.error("An error has occurred: ", { error, errorInfo });
    usePubSub().publish({
      level: "error",
      namespace: PubSubNamespace.unknown,
      message: PubSubMessage.UnknownError,
      payload: { message: error.toString() },
    });
  }

  mount(renderTargetId: string) {
    sendCustomTiming({ name: "chexMountStart" });
    this.elTarget = document.getElementById(renderTargetId);
    if (!this.elTarget) {
      logger.error("Could not find element with id of:", renderTargetId);
      throw new Error("Could not find element with ID: " + renderTargetId);
    }
    if (!this.jwt) {
      throw new Error("Mount error: JWT is not defined");
    }
    const i18nInstance = startI18N({
      namespaces: ["combined"],
      loadPathPrefix: UI_ROOT,
    });

    // Once Split is running then process the language decision
    void getSplitIOSDKPromise().then(() => {
      if (
        getSplitIOFlag({ flagName: "q1-2025-languages", env: TARGET }) !== "on"
      ) {
        // loop through q1 langs (q12025Langs) and remove
        q12025Langs.forEach((lang) => {
          i18nInstance.removeResourceBundle(lang, "combined");
        });
      }
    });

    // For some reason using a timeout to render prevents issues with rapidly mounting, unmounting and then remounting
    setTimeout(() => {
      // will never be false but need to validate due to promise
      if (this.elTarget && this.jwt) {
        this.root = createRoot(this.elTarget);
        const InitElement = createElement(InitWrapperWithProvider, {
          checkoutSessionToken: this.jwt,
          controlledSubmit: this.controlledSubmit,
          themeValueOverrides: this.themeValueOverrides,
          i18nInstance,
          iframePayDomain: this.iframePayDomain,
        });
        const ErrorElement = createElement(
          ErrorBoundary,
          {
            fallback: createElement(CriticalError, {
              i18n: i18nInstance,
            }),
            onError: this.logError.bind(this),
          },
          InitElement
        );
        this.root?.render(ErrorElement);
        if (getIsFeatureEnabled(TARGET, "monitor-diu-removal")) {
          console.debug("Monitoring DIU Removal");
          this.monitorCheckoutIsStillOnPage();
        }
      }
    }, 0);
  }

  /**
   * This will observe the entire document and look to see if an element is removed that has the targeted element as a child.
   * This is trying to catch if a page is changed, to terminate our metrics if DIU is no longer present
   * @private
   */
  private monitorCheckoutIsStillOnPage() {
    // Options for the observer (which mutations to observe)
    const config = { childList: true, subtree: true };

    // Create an observer
    const observer = new MutationObserver((mutationList, observer) => {
      for (const mutation of mutationList) {
        if (mutation.type === "childList") {
          // Walk the removed nodes to see if they contain the target
          for (
            let index = 0;
            index < mutation.removedNodes.length;
            index += 1
          ) {
            if (mutation.removedNodes[index].contains(this.elTarget)) {
              logger.warn(
                "Drop In UI was removed without unmount being called."
              );
              // Stop observing
              observer.disconnect();
              try {
                this.unmount();
              } catch (e) {
                //eat the error
              }
            }
          }
        }
      }
    });
    // Start observing the body for a change
    observer.observe(document.body, config);
  }

  public clearAllSubscribers() {
    usePubSub().clearAllSubscribers();
  }

  public unmount(): void {
    endDatadogSession();

    if (!this.root) {
      throw new Error("Nothing to unmount or it does not exist");
    }
    // Iframe run on its own thread, this will remove and garbage collect all
    // processes running within iframe context.
    this.root.unmount();
  }

  private shouldInit(params?: InitParams): void {
    if (typeof window === "undefined") {
      throw new Error("Window is not defined");
    }
    const url: URL = new URL(window.location.href);
    if (url.searchParams.get("jwt")) {
      this.jwt = url.searchParams.get("jwt");
    } else if (params?.checkoutSessionToken) {
      this.jwt = params.checkoutSessionToken;
    }
    if (!this.jwt) {
      throw new Error("Window or JWT is not defined");
    }

    // This is being disabled due to issues with dev teams unable to run local HTTPS
    // if (url.protocol !== "https:") {
    //   throw new Error(
    //     "Trying to load in unsecure environment. Please run in HTTPS mode."
    //   );
    // }

    const themeOverrideStr = url.searchParams.get("themeValueOverrides");
    if (themeOverrideStr) {
      try {
        this.themeValueOverrides = JSON.parse(themeOverrideStr) as ThemePartial;
      } catch (e) {
        logger.error("Invalid ThemeValueOverride, JSON.parse failed");
      }
    } else if (params?.themeValueOverrides) {
      this.themeValueOverrides = params.themeValueOverrides;
    }
  }

  public subscribe(subscriber: (value: Announcement) => void) {
    return usePubSub().subscribe(subscriber);
  }

  public submit(cartUpdate: Cart | undefined = undefined) {
    if (cartUpdate) {
      this.submitWithUpdatedCart(cartUpdate);
    } else {
      this.publishSubmit();
    }
  }

  private publishSubmit = (
    payload: Record<string, string> | undefined = undefined
  ) => {
    usePubSub().publish({
      level: "command",
      namespace: PubSubNamespace.external,
      message: PubSubMessage.ExternalSubmitTriggered,
      payload,
    });
  };

  private submitWithUpdatedCart = (cart: Cart, currencyCode = "USD") => {
    updateIntentCart({
      // only SMB uses this, and they are limited to USD
      // BE is investigating making it optional
      currencyCode,
      cart,
      checkoutSessionToken: this.jwt ?? "",
    })
      .then((orderDigest) => {
        sendCustomAction({ name: "cartUpdated" });
        this.publishSubmit({
          orderDigest,
        });
      })
      .catch((e) => {
        logger.error("updateIntentCart failed", e);
        usePubSub().publish({
          level: "error",
          message: PubSubMessage.UpdateIntentFailed,
          namespace: PubSubNamespace.payment,
        });
        sendCustomAction({
          name: PubSubMessage.UpdateIntentFailed.toString(),
        });
      });
  };
}

// TODO disable since the query params break caching
// setTimeout(() => {
//   const el = document.createElement("link");
//   el.setAttribute("href", `${UI_ROOT}${FIELD_IFRAME_URL}`);
//   el.rel = "prefetch";
//   document.body.appendChild(el);
// }, 0);

// @ts-ignore
window.DropInUI = DropInUI;
